import { supabase } from '../lib/supabase'
import type {
  ContractCategory,
  ContractCategoryInput,
  SoftDeleteParams,
} from '../types/contractCategories'

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

function throwCategoryError(
  error: { message?: string; code?: string; details?: string; hint?: string } | null,
  fallback = 'Erro ao salvar a categoria.'
): never {
  const code = error?.code || ''
  const blob = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''}`
  if (
    code === '23505' ||
    /contract_categories_name_active_unique|duplicate key/i.test(blob)
  ) {
    throw new Error('Já existe uma categoria com este nome. Altere o nome para salvar.')
  }
  const message = (error?.message || error?.details || '').trim()
  throw new Error(message || fallback)
}

export const contractCategoriesService = {
  async list(includeInactive = true): Promise<ContractCategory[]> {
    let query = supabase
      .from('contract_categories')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as ContractCategory[]
  },

  async create(input: ContractCategoryInput): Promise<ContractCategory> {
    const name = input.name.trim()
    if (!name) throw new Error('Nome é obrigatório.')

    const { data, error } = await supabase
      .from('contract_categories')
      .insert({
        name,
        is_active: input.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throwCategoryError(error, 'Erro ao criar a categoria.')
    return data as ContractCategory
  },

  async update(
    id: string,
    input: Partial<ContractCategoryInput>
  ): Promise<ContractCategory> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) {
      const name = input.name.trim()
      if (!name) throw new Error('Nome é obrigatório.')
      payload.name = name
    }
    if (input.is_active !== undefined) payload.is_active = input.is_active

    const { data, error } = await supabase
      .from('contract_categories')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throwCategoryError(error, 'Erro ao atualizar a categoria.')
    return data as ContractCategory
  },

  async softDelete(id: string, params?: SoftDeleteParams): Promise<void> {
    const { error } = await supabase
      .from('contract_categories')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },
}
